<template>
  <div class="full-wrapper">
    <el-scrollbar>
      <div class="full report-preview" v-loading="loading">
        <el-row type="flex">
          <div class="left">
            <div class="report-container">
              <div class="title">{{ reportInst.reportTmplName }}</div>
              <div class="content-wrapper">
                <span v-for="(item, index) in previewData" :key="index" :class="[item.isDetail ? 'blue' : 'gray']">{{ item.label }}</span>
              </div>
            </div>
          </div>
          <div class="right">
            <span class="title">{{ reportInst.reportName }}</span>
            <div class="r-containter">
              <div class="title-bar" type="flex" justify="space-between">
                <div class="title-h3">보고서 기준 날짜</div>
                <span class="s-text">선택한 날짜 기준으로 분석 보고서가 출력 됩니다.</span>
              </div>
              <div class="vertical-center-box">
                <el-date-picker
                  v-if="reportInst.periodType === 'C'"
                  style="width:250px;"
                  v-model="generateDate"
                  type="daterange"
                  :range-separator="defaultDateRangeOptions.rangeSeparator"
                  :start-placeholder="defaultDateRangeOptions.startPlaceholder"
                  :end-placeholder="defaultDateRangeOptions.endPlaceholder"
                  :default-time="defaultDateRangeOptions.defaultTime"
                  :picker-options="defaultDateRangeOptions.pickerOptions"
                  :clearable="false"
                />
                <el-date-picker v-else v-model="generateDate" type="date" :editable="false" :clearable="false" />
                <el-button class="mr-l4 mr-r4" type="primary" @click="generateReport"><i class="far fa-print"></i> 출력</el-button>
              </div>
              <div class="blank30"></div>
              <div class="list form-list mr-b30">
                <el-col class="title-bar"><div class="title-h3">기본 정보</div></el-col>
                <el-form :model="reportInst" label-width="120px">
                  <el-form-item label="보고서명">{{ reportInst.reportName }}</el-form-item>
                  <el-form-item label="보고서 설명">{{ reportInst.reportContents }}</el-form-item>
                  <el-form-item label="보고서 타입">{{ periodType[reportInst.periodType] }}</el-form-item>
                  <el-form-item v-if="reportInst.periodType === 'C'" label="기준 기간">
                    {{ getPeriodDate(reportInst.periodConfSet) }}
                  </el-form-item>
                  <el-form-item label="보고서 종류">{{ reportTmplTypeName(reportInst.reportTmplType) }}</el-form-item>
                  <el-form-item v-if="reportInst.reportTmplId === 'RPT_SKCC_SUMMARY'" label="최종 생성 시간">
                    {{ reportInst.updateDatetime }}
                    <el-button type="primary" @click="showMaintenanceHistPopup" style="height:20px">조치 계획</el-button>
                  </el-form-item>

                </el-form>
              </div>
              <div v-if="reportInst.periodType && reportInst.periodType !== 'C'" class="list form-list mr-b30">
                <el-col class="title-bar"><div class="title-h3">스케줄링</div></el-col>
                <el-form :model="reportInst" label-width="120px">
                  <el-form-item label="자동생성 여부">{{ reportInst.autoCreateFlag === 'Y' ? '활성' : '비활성' }}</el-form-item>
                  <el-form-item label="자동생성 주기">{{ getAutoCreateData(reportInst) }}</el-form-item>
                  <el-form-item label="보고서 수신 이메일">{{ getValueAfterCheckEmpty(reportInst.reportTargetEmail) }}</el-form-item>
                </el-form>
              </div>
              <div v-if="reportInst.attrData && reportInst.attrData.length > 0" class="list form-list mr-b30">
                <el-col class="title-bar"><div class="title-h3">보고서 옵션</div></el-col>
                <el-form :model="reportInst" label-width="120px">
                  <component
                    v-for="(attr, index) in reportInst.attrData"
                    :class="reportInst.attrData.length - 1 === index ? 'no-stroke' : ''"
                    :key="attr.attrId"
                    :is="getComponentName(attr)"
                    :ref="attr.attrId"
                    :view-type="'PREVIEW'"
                    :default-set-list="getDefaultValue(attr.attrListSet)"
                  >
                  </component>
                </el-form>
              </div>
            </div>
          </div>
          <el-button type="secondary" @click="showUpdateReportPopup">보고서 편집</el-button>
          <w-update-report-popup ref="updateReportPopup" :popup-params="popupParams" @close-popup="closePopup"></w-update-report-popup>
          <w-maintenance-hist-popup ref="maintenanceHistPopup" :popup-params="popupParams" @close-popup="closePopup"></w-maintenance-hist-popup>
        </el-row>
      </div>
    </el-scrollbar>
  </div>
</template>

<script>
import isNil from 'lodash/isNil';
import forEach from 'lodash/forEach';
import capitalize from 'lodash/capitalize';
import isEmpty from 'lodash/isEmpty';
import findIndex from 'lodash/findIndex';
import { mapGetters } from 'vuex';
import reportApi from '@/api/report/report';
import { getDateToString, defaultDateRangeOptions, getDate } from '@/utils/date';
import { getValueAfterCheckEmpty } from '@/utils/string';
import msgUtil from '@/utils/message';
import { TIME, MONTH, WEEK, INTERVAL_FIVE_MINUTE } from '@/constants/date';
import WDetailInfoComponent from '@/views/report/attr/DetailInfoComponent';
import WSortComponent from '@/views/report/attr/SortComponent';
import WWatchallGroupsTable from '@/views/report/attr/WatchallGroupsTable';
import WWatchallGroupTable from '@/views/report/attr/WatchallGroupTable';
import WWatchallObjsTree from '@/views/report/attr/WatchallObjsTree';
import WWatchallResourceComponent from '@/views/report/attr/WatchallResourceComponent';
import WUpdateReportPopup from '../popup/UpdateReportPopup';
import WMaintenanceHistPopup from '../popup/MaintenanceHistPopup';

export default {
  name: 'WReportDetailView',
  components: {
    WDetailInfoComponent,
    WSortComponent,
    WWatchallGroupsTable,
    WWatchallGroupTable,
    WWatchallObjsTree,
    WWatchallResourceComponent,
    WUpdateReportPopup,
    WMaintenanceHistPopup,
  },
  props: {
    reportBasicInfo: {
      type: Object,
      default() {
        return {
          autoCreateFlag: 'N',
        };
      },
    },
    reportOptionInfo: {
      type: Object,
      default: () => {},
    },
    attrData: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      defaultDateRangeOptions,
      periodType: {
        D: '일간',
        W: '주간',
        M: '월간',
        Q: '분기',
        H: '반기',
        Y: '연간',
        C: '사용자 정의',
      },
      popupParams: {},
      reportInst: {},
      generateDate: new Date(),
      loading: false,
    };
  },
  computed: {
    previewData() {
      return isNil(this.reportInst.reportPreviewData) ? [] : JSON.parse(this.reportInst.reportPreviewData);
    },
    ...mapGetters(['selectedNode']),
  },
  watch: {
    selectedNode: {
      handler() {
        this.generateDate = new Date();
        this.loadReport();
      },
      immediate: true,
    },
  },
  methods: {
    getValueAfterCheckEmpty,
    loadReport() {
      const params = {
        reportId: this.selectedNode.reportId,
        reportTmplId: this.selectedNode.reportTmplId,
      };
      this.loading = true;
      reportApi
        .getReportInstData(params)
        .then((response) => {
          if (response.data.success) {
            this.reportInst = response.data.data;
            this.$nextTick(() => {
              const attrList = this.reportInst.attrData;
              const confSet = JSON.parse(this.reportInst.reportConfSet);
              forEach(attrList, (item) => {
                this.$refs[item.attrId][0].setOptionInfo(confSet[item.attrId]);
              });

              if (this.reportInst.periodType === 'C') {
                const periodDate = this.reportInst.periodConfSet.split(',');
                const startTime = getDate(periodDate[0]);
                const endTime = getDate(periodDate[1]);
                this.generateDate = [startTime, endTime];
              }
            });
          } else {
            this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '보고서 정보', false));
          }
        })
        .catch(() => {
          this.$message(msgUtil.getResultMsgData(msgUtil.action.VIEW, '보고서 정보', false));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    generateReport() {
      const report = this.reportInst;
      let generateDate;
      if (report.periodType === 'C') {
        const startTime = getDateToString(this.generateDate[0], 'YYYY-MM-DD 00:00:00');
        const endTime = getDateToString(this.generateDate[1], 'YYYY-MM-DD 23:59:59');
        generateDate = `${startTime},${endTime}`;
      } else {
        generateDate = getDateToString(this.generateDate, 'YYYY-MM-DD 00:00:00');
      }
      const params = {
        reportId: report.reportId,
        reportTmplId: report.reportTmplId,
        reportTmplPath: report.reportTmplPath,
        reportName: report.reportName,
        reportContents: report.reportContents,
        reportConfSet: report.reportConfSet,
        reportTmplType: report.reportTmplType,
        periodType: report.periodType,
        periodConfSet: report.periodConfSet,
        historyFlag: 'Y',
        generateDate,
      };

      reportApi.generateReport(params);
    },
    showUpdateReportPopup() {
      const report = this.reportInst;
      const popupParams = {
        label: report.reportTmplName,
        reportId: report.reportId,
        treeKey: this.selectedNode.treeKey,
        periodDataSet: this.selectedNode.periodDataSet,
        basicInfo: {
          reportTmplId: report.reportTmplId,
          reportName: report.reportName,
          reportContents: report.reportName,
          periodType: report.periodType,
          periodDate: [new Date(), new Date()],
          autoCreateFlag: report.autoCreateFlag,
          reportTargetEmail: '',
          autoCreateHour: 0,
          autoCreateDay: 0,
          autoCreateMonth: 1,
          autoCreateDate: 1,
        },
        // attrData: report.attrData,
      };

      if (report.periodType === 'C' && !isNil(report.periodConfSet)) {
        const periodDate = report.periodConfSet.split(',');
        const startDatetime = getDate(periodDate[0]);
        const endDatetime = getDate(periodDate[1]);
        popupParams.basicInfo.periodDate = [startDatetime, endDatetime];
      }

      if (report.autoCreateFlag === 'Y') {
        popupParams.basicInfo.reportTargetEmail = report.reportTargetEmail;
        popupParams.basicInfo.autoCreateHour = report.autoCreateHour;
        if (report.periodType === 'W') {
          popupParams.basicInfo.autoCreateDay = report.autoCreateDay;
        } else if (report.periodType === 'M') {
          popupParams.basicInfo.autoCreateDate = report.autoCreateDate;
        } else if (report.periodType !== 'D') {
          popupParams.basicInfo.autoCreateDate = report.autoCreateDate;
          popupParams.basicInfo.autoCreateMonth = report.autoCreateMonth;
        }
      }

      const attrData = report.attrData;
      const reportConfSet = JSON.parse(report.reportConfSet);
      forEach(attrData, (item) => {
        item.attrDefaultValue = JSON.stringify(reportConfSet[item.attrId]);
      });
      popupParams.attrData = attrData;

      this.popupParams = popupParams;
      this.$refs.updateReportPopup.open();
    },
    closePopup(treeKey) {
      this.$emit('load-tree-data', treeKey);
    },
    getPeriodDate(periodConfSet) {
      if (isNil(periodConfSet)) {
        return '';
      }
      const periodDate = periodConfSet.split(',');
      const startTime = getDate(periodDate[0]);
      const endTime = getDate(periodDate[1]);
      return `${getDateToString(startTime)} ~ ${getDateToString(endTime)}`;
    },
    /**
     * 템플릿 별 Attribute에 따른 ComponentName 반환
     * @author kimjy
     * @param  {Object} attr Attribute 정보
     * @return {String}      componentName
     */
    getComponentName(attr) {
      const componentNames = attr.attrId.split('_');
      let componentName = 'W';
      forEach(componentNames, (item) => {
        componentName = `${componentName}${capitalize(item)}`;
      });
      return `${componentName}${capitalize(attr.attrTypeId)}`;
    },
    /**
     * 기본 값 설정에 필요한 String 데이터 JSON 변환
     * @author kimjy
     * @param  {String} defaultValueStr 기본 설정 값
     */
    getDefaultValue(defaultValueStr) {
      if (!isEmpty(defaultValueStr)) {
        return JSON.parse(defaultValueStr);
      }
      return null;
    },
    getAutoCreateData(basicInfo) {
      if (isNil(basicInfo.autoCreateFlag) || basicInfo.autoCreateFlag === 'N') {
        return getValueAfterCheckEmpty();
      }
      let autoCreateData = `${TIME[findIndex(TIME, ['time', basicInfo.autoCreateHour])].label}시 ${INTERVAL_FIVE_MINUTE[findIndex(INTERVAL_FIVE_MINUTE, ['minute', basicInfo.autoCreateMinute])].label} 분`;
      if (basicInfo.periodType === 'W') {
        autoCreateData = `${WEEK[findIndex(WEEK, ['week', basicInfo.autoCreateDay])].label} ${autoCreateData}`;
      } else if (basicInfo.periodType === 'M') {
        autoCreateData = `${basicInfo.autoCreateDate}일 ${autoCreateData}`;
      } else if (basicInfo.periodType !== 'D') {
        autoCreateData = `${basicInfo.autoCreateDate}일 ${autoCreateData}`;
        autoCreateData = `${MONTH[findIndex(MONTH, ['value', basicInfo.autoCreateMonth])].label} ${autoCreateData}`;
      }
      return autoCreateData;
    },

    reportTmplTypeName(reportTmplType) {
      if (reportTmplType === 'OZ') {
        return '오즈';
      } else if (reportTmplType === 'EXCEL') {
        return '엑셀';
      }
      return reportTmplType;
    },

    showMaintenanceHistPopup() {
      const report = this.reportInst;

      const popupParams = {
        label: report.reportName,
        reportId: report.reportId,
        reportTmplId: report.reportTmplId,
        reportTmplPath: report.reportTmplPath,
        reportName: report.reportName,
        reportContents: report.reportContents,
        reportConfSet: report.reportConfSet,
        reportTmplType: report.reportTmplType,
        periodType: report.periodType,
        periodConfSet: report.periodConfSet,
        historyFlag: 'Y',
        generateDate: getDateToString(this.generateDate, 'YYYY-MM-DD 00:00:00'),
      };

      const attrData = report.attrData;
      const reportConfSet = JSON.parse(report.reportConfSet);
      forEach(attrData, (item) => {
        item.attrDefaultValue = JSON.stringify(reportConfSet[item.attrId]);
      });
      popupParams.attrData = attrData;
      this.popupParams = popupParams;
      this.$refs.maintenanceHistPopup.open();
    },
    getGenerateReportDisabled() {
      const report = this.reportInst;
      let disabled = false;
      if (report.reportTmplId === 'RPT_SKCC_DETAIL') {
        disabled = true;
      } else {
        disabled = false;
      }

      return disabled;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/report/report/index.vue